import { useRef } from "react";

const editorStyles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--admin-gap-lg)",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--admin-gap-lg)",
  },
  title: {
    margin: 0,
    fontSize: "var(--admin-heading-size)",
    fontWeight: 700,
    color: "var(--text-main)",
  },
  hint: {
    margin: 0,
    fontSize: "var(--admin-text-sm)",
    color: "var(--text-muted)",
  },
  section: {
    border: "1px solid var(--border)",
    borderRadius: "var(--admin-card-radius)",
    padding: "var(--admin-card-padding)",
    backgroundColor: "var(--card-bg-soft)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--admin-gap)",
  },
  sectionHeader: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "var(--admin-gap)",
    alignItems: "center",
  },
  input: {
    width: "100%",
    minHeight: "var(--admin-control-height)",
    padding: "var(--admin-control-padding-y) var(--admin-control-padding-x)",
    borderRadius: "var(--admin-radius)",
    border: "1px solid var(--border)",
    fontSize: "var(--admin-text)",
    color: "var(--text-main)",
    boxSizing: "border-box",
    backgroundColor: "var(--field-bg)",
    outline: "none",
    caretColor: "var(--accent)",
  },
  tableHead: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr 1fr var(--admin-control-height)",
    gap: "var(--admin-gap-sm)",
    fontSize: "var(--admin-text-xs)",
    fontWeight: 700,
    color: "var(--text-muted)",
    padding: "0 2px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr 1fr var(--admin-control-height)",
    gap: "var(--admin-gap-sm)",
    alignItems: "center",
  },
  addButton: {
    border: "1px solid var(--border-strong)",
    backgroundColor: "var(--card-bg)",
    color: "var(--text-soft)",
    borderRadius: "var(--admin-radius)",
    padding: "var(--admin-button-padding-y) var(--admin-button-padding-x)",
    fontSize: "var(--admin-text-sm)",
    fontWeight: 600,
    cursor: "pointer",
  },
  darkButton: {
    border: "none",
    backgroundColor: "var(--accent)",
    color: "var(--button-text)",
    borderRadius: "var(--admin-radius)",
    padding: "var(--admin-button-padding-y) var(--admin-button-padding-x)",
    fontSize: "var(--admin-text-sm)",
    fontWeight: 600,
    cursor: "pointer",
  },
  dangerButton: {
    border: "1px solid var(--danger)",
    backgroundColor: "var(--danger-soft)",
    color: "var(--danger)",
    borderRadius: "var(--admin-radius)",
    padding: "var(--admin-button-padding-y) var(--admin-button-padding-x)",
    fontSize: "var(--admin-text-sm)",
    fontWeight: 600,
    cursor: "pointer",
  },
  iconButton: {
    width: "var(--admin-control-height)",
    height: "var(--admin-control-height)",
    border: "1px solid var(--danger)",
    backgroundColor: "var(--danger-soft)",
    color: "var(--danger)",
    borderRadius: "var(--admin-radius)",
    fontSize: "16px",
    fontWeight: 700,
    cursor: "pointer",
  },
};

function makeId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function createEmptyRow() {
  return {
    id: makeId("price"),
    type: "",
    area_from_m2: "",
    price_from_mln: "",
  };
}

export function pricesToSections(prices) {
  if (!prices || typeof prices !== "object" || Array.isArray(prices)) {
    return [];
  }

  return Object.entries(prices).map(([title, rows]) => ({
    id: makeId("section"),
    title,
    rows: Array.isArray(rows)
      ? rows.map((row) => ({
          id: makeId("price"),
          type: row?.type ?? "",
          area_from_m2: row?.area_from_m2 ?? row?.area ?? "",
          price_from_mln: row?.price_from_mln ?? row?.price_mln ?? "",
        }))
      : [],
  }));
}

function parseNumber(value, label) {
  const text = String(value ?? "").trim().replace(",", ".");

  if (!text) {
    return undefined;
  }

  const number = Number(text);

  if (!Number.isFinite(number)) {
    throw new Error(`${label} должно быть числом.`);
  }

  return number;
}

export function sectionsToPrices(sections) {
  const result = {};
  const usedTitles = new Set();

  for (const section of sections || []) {
    const title = String(section.title ?? "").trim();

    if (!title) {
      continue;
    }

    if (usedTitles.has(title)) {
      throw new Error(`Раздел цен "${title}" повторяется.`);
    }

    usedTitles.add(title);

    const rows = (section.rows || [])
      .map((row) => {
        const type = String(row.type ?? "").trim();
        const areaRaw = String(row.area_from_m2 ?? "").trim();
        const priceRaw = String(row.price_from_mln ?? "").trim();

        if (!type && !areaRaw && !priceRaw) {
          return null;
        }

        return {
          type,
          area_from_m2: parseNumber(areaRaw, `Площадь в разделе "${title}"`),
          price_from_mln: parseNumber(priceRaw, `Цена в разделе "${title}"`),
        };
      })
      .filter(Boolean);

    if (rows.length > 0) {
      result[title] = rows;
    }
  }

  return result;
}

function PricesEditor({ value, onChange, disabled }) {
  const wrapperRef = useRef(null);
  const sections = Array.isArray(value) ? value : [];

  const updateSections = (nextSections) => {
    onChange(nextSections);
  };

  const addSection = () => {
    updateSections([
      ...sections,
      {
        id: makeId("section"),
        title: "",
        rows: [createEmptyRow()],
      },
    ]);
  };

  const removeSection = (sectionId) => {
    updateSections(sections.filter((section) => section.id !== sectionId));
  };

  const updateSectionTitle = (sectionId, title) => {
    updateSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, title } : section
      )
    );
  };

  const addRow = (sectionId) => {
    updateSections(
      sections.map((section) =>
        section.id === sectionId
          ? { ...section, rows: [...section.rows, createEmptyRow()] }
          : section
      )
    );
  };

  const removeRow = (sectionId, rowId) => {
    updateSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              rows: section.rows.filter((row) => row.id !== rowId),
            }
          : section
      )
    );
  };

  const updateRow = (sectionId, rowId, field, fieldValue) => {
    updateSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              rows: section.rows.map((row) =>
                row.id === rowId ? { ...row, [field]: fieldValue } : row
              ),
            }
          : section
      )
    );
  };

  const handlePriceInputKeyDown = (event) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }

    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    const inputs = Array.from(
      wrapper.querySelectorAll('input[data-price-input="true"]:not(:disabled)')
    );
    const currentIndex = inputs.indexOf(event.currentTarget);
    const nextIndex =
      event.key === "ArrowDown" ? currentIndex + 1 : currentIndex - 1;
    const nextInput = inputs[nextIndex];

    if (!nextInput) {
      return;
    }

    event.preventDefault();
    nextInput.focus();
    nextInput.select();
  };

  return (
    <div className="prices-editor" ref={wrapperRef} style={editorStyles.wrapper}>
      <div className="prices-editor__top-row" style={editorStyles.topRow}>
        <div>
          <p style={editorStyles.hint}>
            Раздел — это срок и отделка, внутри него строки по типам квартир.
          </p>
        </div>

        <button
          className="prices-editor-button prices-editor-button--primary"
          disabled={disabled}
          onClick={addSection}
          style={{
            ...editorStyles.darkButton,
            opacity: disabled ? 0.7 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
          type="button"
        >
          + Раздел цен
        </button>
      </div>

      {sections.length === 0 ? (
        <button
          className="prices-editor-button"
          disabled={disabled}
          onClick={addSection}
          style={editorStyles.addButton}
          type="button"
        >
          Добавить цены
        </button>
      ) : null}

      {sections.map((section) => (
        <div className="prices-editor__section" key={section.id} style={editorStyles.section}>
          <div style={editorStyles.sectionHeader}>
            <input
              className="prices-editor-control"
              data-price-input="true"
              disabled={disabled}
              onKeyDown={handlePriceInputKeyDown}
              onChange={(event) =>
                updateSectionTitle(section.id, event.target.value)
              }
              placeholder="Например: 1 кв 2026 (без отделки)"
              style={editorStyles.input}
              type="text"
              value={section.title}
            />

            <button
              className="prices-editor-button prices-editor-button--danger"
              disabled={disabled}
              onClick={() => removeSection(section.id)}
              style={editorStyles.dangerButton}
              type="button"
            >
              Удалить раздел
            </button>
          </div>

          <div className="prices-editor__table-head" style={editorStyles.tableHead}>
            <span>Тип</span>
            <span>Площадь от, м²</span>
            <span>Цена от, млн</span>
            <span />
          </div>

          {section.rows.map((row) => (
            <div className="prices-editor__row" key={row.id} style={editorStyles.row}>
              <input
                className="prices-editor-control"
                data-price-input="true"
                disabled={disabled}
                onKeyDown={handlePriceInputKeyDown}
                onChange={(event) =>
                  updateRow(section.id, row.id, "type", event.target.value)
                }
                placeholder="1к"
                style={editorStyles.input}
                type="text"
                value={row.type}
              />

              <input
                className="prices-editor-control"
                data-price-input="true"
                disabled={disabled}
                inputMode="decimal"
                onKeyDown={handlePriceInputKeyDown}
                onChange={(event) =>
                  updateRow(
                    section.id,
                    row.id,
                    "area_from_m2",
                    event.target.value
                  )
                }
                placeholder="40"
                style={editorStyles.input}
                type="text"
                value={row.area_from_m2}
              />

              <input
                className="prices-editor-control"
                data-price-input="true"
                disabled={disabled}
                inputMode="decimal"
                onKeyDown={handlePriceInputKeyDown}
                onChange={(event) =>
                  updateRow(
                    section.id,
                    row.id,
                    "price_from_mln",
                    event.target.value
                  )
                }
                placeholder="27.4"
                style={editorStyles.input}
                type="text"
                value={row.price_from_mln}
              />

              <button
                className="prices-editor-icon-button"
                disabled={disabled}
                onClick={() => removeRow(section.id, row.id)}
                style={editorStyles.iconButton}
                title="Удалить строку"
                type="button"
              >
                ×
              </button>
            </div>
          ))}

          <button
            className="prices-editor-button"
            disabled={disabled}
            onClick={() => addRow(section.id)}
            style={editorStyles.addButton}
            type="button"
          >
            + Строка цены
          </button>
        </div>
      ))}
    </div>
  );
}

export default PricesEditor;
